import {
  DeckType,
  GenerateRequest,
  JobStatusResponse,
  JobSubmitResponse,
  RefineStatusResponse,
  RefineSubmitResponse,
  TargetType,
} from '~/pages/BriefCase/types';

import { json_headers, request } from './common';
import { readStudioBqReadPath } from './studioBqReadPath';

const STUDIO_API_PATH = '/api/studio';
const OBJECT_URL_REVOKE_DELAY_MS = 1000;

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

const BASE_PATH = window.configs.STUDIO_API_BASE_URL
  ? trimTrailingSlash(window.configs.STUDIO_API_BASE_URL)
  : `${trimTrailingSlash(window.configs.API_HOST)}${STUDIO_API_PATH}`;

function isDirectArtifactUrl(path: string): boolean {
  if (path.startsWith('data:') || path.startsWith('blob:')) {
    return true;
  }
  if (!path.startsWith('http://') && !path.startsWith('https://')) {
    return false;
  }
  try {
    return !new URL(path).pathname.startsWith(STUDIO_API_PATH);
  } catch {
    return true;
  }
}

function resolveStudioPath(path: string): string {
  if (
    path.startsWith('data:') ||
    path.startsWith('blob:') ||
    path.startsWith('http://') ||
    path.startsWith('https://')
  ) {
    return path;
  }
  if (path.startsWith(STUDIO_API_PATH)) {
    return `${BASE_PATH}${path.slice(STUDIO_API_PATH.length)}`;
  }
  if (path.startsWith('/')) {
    return `${BASE_PATH}${path}`;
  }
  return `${BASE_PATH}/${path}`;
}

interface StudioArtifactAccess {
  url: string;
  expiresIn: number;
  direct: boolean;
}

function getStudioArtifactAccess(accessUrl: string, options?: { signal?: AbortSignal }): Promise<StudioArtifactAccess> {
  return request(resolveStudioPath(accessUrl), {
    method: 'GET',
    signal: options?.signal,
  });
}

function fetchStudioArtifact(path: string, options?: { signal?: AbortSignal }): Promise<Blob> {
  if (isDirectArtifactUrl(path)) {
    return Promise.reject(new Error('Refusing to fetch direct artifact URL through authenticated Studio request'));
  }
  return request(resolveStudioPath(path), {
    method: 'GET',
    download: true,
    signal: options?.signal,
  });
}

interface StudioDisplayArtifact {
  url: string;
  revoke?: () => void;
}

export async function getStudioArtifactDisplayUrl(
  accessUrl: string,
  options?: { signal?: AbortSignal },
): Promise<StudioDisplayArtifact> {
  const access = await getStudioArtifactAccess(accessUrl, options);
  if (access.direct) {
    return { url: access.url };
  }

  const blob = await fetchStudioArtifact(access.url, options);
  const objectUrl = URL.createObjectURL(blob);
  return {
    url: objectUrl,
    revoke: () => URL.revokeObjectURL(objectUrl),
  };
}

function triggerArtifactDownload(href: string, filename: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadStudioArtifact(
  accessUrl: string,
  filename: string,
  options?: { signal?: AbortSignal },
): Promise<void> {
  const access = await getStudioArtifactAccess(accessUrl, options);
  if (access.direct) {
    triggerArtifactDownload(access.url, filename);
    return;
  }

  const blob = await fetchStudioArtifact(access.url, options);
  const objectUrl = URL.createObjectURL(blob);
  triggerArtifactDownload(objectUrl, filename);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS);
}

export interface StudioOfferSearchRow {
  id: string;
  name: string;
  propertyId?: string | null;
  vendorName?: string | null;
  /** Vendor business key (sales_with_refunds.vendor_bk). Experiences only for now. */
  vendorBk?: string | null;
  locations?: Array<string>;
  productName?: string | null;
  offerStartDate?: string | null;
  offerEndDate?: string | null;
  vendorCurrency?: string | null;
  prevOfferBk?: string | null;
  prevOfferName?: string | null;
}

export interface StudioOfferSearchResponse {
  results?: Array<StudioOfferSearchRow>;
  total?: number;
}

export type StudioProductLine = 'hotel' | 'experiences';

export type StudioTargetType = TargetType;

export function searchOffers(
  query: string,
  propertyScope?: string | null,
  currencyScope?: string | null,
  options?: { signal?: AbortSignal; productLine?: StudioProductLine; targetType?: StudioTargetType },
): Promise<StudioOfferSearchResponse> {
  const params = new URLSearchParams({ q: query, limit: '15' });
  if (propertyScope?.trim()) {
    params.set('propertyId', propertyScope.trim());
  }
  if (currencyScope?.trim()) {
    params.set('currency', currencyScope.trim().toUpperCase());
  }
  if (options?.productLine && options.productLine !== 'hotel') {
    params.set('productLine', options.productLine);
  }
  if (options?.targetType && options.targetType !== 'offer') {
    params.set('targetType', options.targetType);
  }
  const bqReadPath = readStudioBqReadPath();
  if (bqReadPath) {
    params.set('bqReadPath', bqReadPath);
  }
  return request(`${BASE_PATH}/offers/search?${params.toString()}`, { method: 'GET', signal: options?.signal });
}

export function generateDeck(payload: GenerateRequest): Promise<JobSubmitResponse> {
  const bqReadPath = readStudioBqReadPath();
  return request(`${BASE_PATH}/generate`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(bqReadPath ? { ...payload, bqReadPath } : payload),
  });
}

export function getDeckJob(jobId: string, options?: { signal?: AbortSignal }): Promise<JobStatusResponse> {
  return request(`${BASE_PATH}/jobs/${jobId}`, { method: 'GET', signal: options?.signal });
}

export interface RefineSlidePayload {
  jobId: string;
  index: number;
  comment: string;
}

/**
 * Submit a slide refine. Returns 202 with a refine_job_id; poll
 * {@link getRefineStatus} until status is `complete` or terminal.
 */
export function submitRefineSlide(payload: RefineSlidePayload): Promise<RefineSubmitResponse> {
  return request(`${BASE_PATH}/decks/${payload.jobId}/slides/${payload.index}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify({ comment: payload.comment }),
  });
}

export function getRefineStatus(
  jobId: string,
  index: number,
  refineId: string,
  options?: { signal?: AbortSignal },
): Promise<RefineStatusResponse> {
  return request(`${BASE_PATH}/decks/${jobId}/slides/${index}/refines/${refineId}`, {
    method: 'GET',
    signal: options?.signal,
  });
}

export interface TemplateVersion {
  versionId: string;
  downloadAccessUrl: string;
  isLatest: boolean;
  lastModified: string;
  size: number;
  etag: string;
}

export interface TemplateVersionsResponse {
  bucket: string;
  key: string;
  versions: Array<TemplateVersion>;
}

export interface TemplateUploadResponse {
  bucket: string;
  key: string;
  versionId: string;
  etag: string;
  size: number;
}

export interface TemplateUploadInit {
  uploadId: string;
  uploadUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresIn: number;
}

const TEMPLATE_PATH = `${BASE_PATH}/admin/template`;

/**
 * Each deck type has its own canonical template object — and therefore its own
 * S3 version history — so every template endpoint is scoped by `deckType`.
 */
function templatePath(deckType: DeckType): string {
  return `${TEMPLATE_PATH}/${deckType}`;
}

export function listTemplateVersions(
  deckType: DeckType,
  options?: { signal?: AbortSignal },
): Promise<TemplateVersionsResponse> {
  return request(`${templatePath(deckType)}/versions`, { method: 'GET', signal: options?.signal });
}

/**
 * Step 1: mint a presigned PUT URL for staging a new template for `deckType`.
 * The upload itself goes browser → S3 to bypass the 10MB edge proxy cap;
 * svc-studio never sees the bytes until `commitTemplateUpload`.
 */
export function createTemplateUpload(deckType: DeckType): Promise<TemplateUploadInit> {
  return request(`${templatePath(deckType)}/uploads`, {
    method: 'POST',
    headers: json_headers,
  });
}

/**
 * Step 2: PUT the file body straight to S3 using the presigned URL.
 * Bypasses our `request()` helper deliberately — no auth header, no
 * credentials (cross-origin to S3), and the `Content-Type` must match
 * what was bound into the signature exactly.
 */
export async function putTemplateToS3(init: TemplateUploadInit, file: File): Promise<void> {
  const response = await fetch(init.uploadUrl, {
    method: init.method,
    body: file,
    headers: init.headers,
    credentials: 'omit',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`S3 upload failed (${response.status}): ${text || response.statusText}`);
  }
}

/** Step 3: validate the staged PPTX and promote it to the deck type's canonical key. */
export function commitTemplateUpload(deckType: DeckType, uploadId: string): Promise<TemplateUploadResponse> {
  return request(`${templatePath(deckType)}/uploads/${encodeURIComponent(uploadId)}/commit`, {
    method: 'POST',
    headers: json_headers,
  });
}
